import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const ViewResultModal = ({ open, onOpenChange, examResult, examName }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const QUESTIONS_PER_PAGE = 5;

  if (!examResult) return null;

  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getAnswerDisplay = (answer) => {
    if (Array.isArray(answer)) {
      return answer.join(", ");
    }
    return answer?.toString() || "Not answered";
  };

  const getReviewStatusBadge = (status) => {
    const configs = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Under Review" },
      reviewed: { color: "bg-green-100 text-green-800", text: "Reviewed" },
      flagged: { color: "bg-red-100 text-red-800", text: "Flagged" },
    };
    const config = configs[status] || configs.pending;

    return (
      <Badge variant="secondary" className={config.color}>
        {config.text}
      </Badge>
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // You can add API call here to fetch questions for the specific page
  };

  const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
  const endIndex = startIndex + QUESTIONS_PER_PAGE;
  const paginatedQuestions = examResult.questionResults.slice(startIndex, endIndex);
  const totalPages = Math.ceil(examResult.questionResults.length / QUESTIONS_PER_PAGE);
  const percentageScore =
  examResult.totalQuestions && examResult.totalQuestions > 0
    ? Math.round((examResult.correctAnswers / examResult.totalQuestions) * 100)
    : 0;


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Exam Result: {examName || examResult.examId?.name}
          </DialogTitle>
          <DialogDescription>
            Detailed results and analysis of your exam performance
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Overall Result Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Overall Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {percentageScore}%
                  </div>
                  <div className="text-sm text-gray-600">Overall Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {examResult.correctAnswers || 0}
                  </div>
                  <div className="text-sm text-gray-600">Correct Answers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-600">
                    {examResult.totalQuestions || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Questions</div>
                </div>
                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${examResult.isPassed ? "text-green-600" : "text-red-600"
                      }`}
                  >
                    {examResult.isPassed ? "PASSED" : "FAILED"}
                  </div>
                  <div className="text-sm text-gray-600">Result</div>
                </div>
              </div>

              {/* Additional Info */}
              <Separator className="my-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>
                    Pass Percentage: {examResult.examId?.passPercentage || 50}%
                  </span>
                </div>
                {examResult.reviewStatus && (
                  <div className="flex items-center gap-2">
                    <span>Review Status:</span>
                    {getReviewStatusBadge(examResult.reviewStatus)}
                  </div>
                )}
              </div>

              {examResult.disqualified && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <XCircle className="h-4 w-4" />
                    <span className="font-semibold">Disqualified</span>
                  </div>
                  <p className="text-sm text-red-700 mt-1">
                    {examResult.disqualificationReason ||
                      "Exam was terminated due to policy violations"}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Question-wise Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Question-wise Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              {examResult.questionAnalysis &&
                examResult.questionAnalysis.length > 0 ? (
                <>
                  <Accordion type="single" collapsible className="w-full">
                    {paginatedQuestions.map((question, index) => {
                      if (!question) {
    console.warn(`Missing question at index ${startIndex + index}`);
    return null;
  }
                      return (
                      <AccordionItem key={question.questionNumber || index} value={`question-${question.questionNumber || index}`}>
                        <AccordionTrigger className="text-left">
                          <div className="flex items-center gap-3 w-full">
                            <div className="flex items-center gap-2">
                              {question.isCorrect ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600" />
                              )}
                              <span className="font-medium">
                                Question {question.questionNumber}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 ml-auto mr-4">
                              <Badge
                                variant={
                                  question.isCorrect ? "default" : "destructive"
                                }
                              >
                                {question.marks} marks
                              </Badge>
                              <span className="text-sm text-gray-500">
                                {formatTime(question.timeSpent)}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {/* ... keep existing code (question content display logic) */}
                          <div className="space-y-4 pt-2">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">
                                Question:
                              </h4>
                              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                                {question.question}
                              </p>
                            </div>

                            {question.choices &&
                              question.choices.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2">
                                    Options:
                                  </h4>
                                  <div className="space-y-2">
                                    {question.choices.map(
                                      (choice, choiceIndex) => (
                                        <div
                                          key={choiceIndex}
                                          className={`p-2 rounded border ${getAnswerDisplay(
                                            question.candidateAnswer
                                          ) === choice
                                            ? question.isCorrect
                                              ? "bg-green-50 border-green-200"
                                              : "bg-red-50 border-red-200"
                                            : getAnswerDisplay(
                                              question.correctAnswer
                                            ) === choice
                                              ? "bg-blue-50 border-blue-200"
                                              : "bg-gray-50 border-gray-200"
                                            }`}
                                        >
                                          <span className="text-sm">
                                            {String.fromCharCode(
                                              65 + choiceIndex
                                            )}
                                            . {choice}
                                            {getAnswerDisplay(
                                              question.candidateAnswer
                                            ) === choice && (
                                                <span className="ml-2 text-xs font-medium text-blue-600">
                                                  (Your answer)
                                                </span>
                                              )}
                                            {getAnswerDisplay(
                                              question.correctAnswer
                                            ) === choice && (
                                                <span className="ml-2 text-xs font-medium text-green-600">
                                                  (Correct answer)
                                                </span>
                                              )}
                                          </span>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                  Your Answer:
                                </h4>
                                <p
                                  className={`p-2 rounded text-sm ${question.isCorrect
                                    ? "bg-green-50 text-green-800"
                                    : "bg-red-50 text-red-800"
                                    }`}
                                >
                                  {getAnswerDisplay(question.candidateAnswer)}
                                </p>
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-2">
                                  Correct Answer:
                                </h4>
                                <p className="p-2 bg-blue-50 text-blue-800 rounded text-sm">
                                  {getAnswerDisplay(question.correctAnswer)}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                              <div className="flex items-center gap-2">
                                {question.isCorrect ? (
                                  <Badge
                                    variant="default"
                                    className="bg-green-600"
                                  >
                                    Correct
                                  </Badge>
                                ) : (
                                  <Badge variant="destructive">Incorrect</Badge>
                                )}
                                <span className="text-sm text-gray-600">
                                  Time spent: {formatTime(question.timeSpent)}
                                </span>
                              </div>
                              <span className="text-sm font-medium">
                                {question.marks} / {question.marks} marks
                              </span>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      );
})}
                  </Accordion>

                  {/* Pagination for Questions */}
                  {totalPages > 1 && (
                    <div className="mt-6">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (currentPage > 1) {
                                  handlePageChange(currentPage - 1);
                                }
                              }}
                            />
                          </PaginationItem>
                          {[...Array(totalPages)].map((_, i) => (
                            <PaginationItem key={i}>
                              <PaginationLink
                                href="#"
                                isActive={currentPage === i + 1}
                                className={`px-3 py-1 rounded-md transition-colors duration-200 ${currentPage === i + 1
                                  ? "bg-blue-600 text-white"  // Highlight active page
                                  : "bg-gray-100 text-gray-800 hover:bg-blue-100"
                                  }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(i + 1);
                                }}
                              >
                                {i + 1}
                              </PaginationLink>
                            </PaginationItem>
                          ))}
                          <PaginationItem>
                            <PaginationNext
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (currentPage < totalPages) {
                                  handlePageChange(currentPage + 1);
                                }
                              }}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Question-wise analysis not available for this exam.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewResultModal;
